import React, { FC, useContext } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { WebcastResponse } from '../../libs/fetchWebcast'
import { ChannelResponse } from '../../libs/fetchChannel'
import { WebcastContext } from '../../contexts/WebcastContext'
import { ChannelContext } from '../../contexts/ChannelContext'
import { WebcastCommunitiesContext } from '../../contexts/WebcastCommunitiesContext'
import getCanonicalUrl from '../../libs/getCanonicalUrl'
import getStructuredData from '../../libs/getStructuredData'
import getWebcastPrimaryCommunity from '../../libs/getWebcastPrimaryCommunity'
import useGigIDs from '../../libs/useGigIDs'
import getGigIDsFromAsPath from '../../libs/getGigIDsFromAsPath/getGigIDsFromAsPath'
import consentScript from '../Consent/consent'
import consentCssScript from '../Consent/consentCss.js'
import { EnvVariablesContext } from '../../contexts/EnvVariablesContext'
// import privacyScript from '../Consent/privacy'

function trimMetaDescription(description: string) {

  const MAX_LENGTH = 155

  if (description?.length <= MAX_LENGTH) return description
  return `${description?.substr(0, MAX_LENGTH - 3)}...`

}

export function getRobotsMeta(webcastData: WebcastResponse, channelData: ChannelResponse, query: { [key: string]: string | undefined }): 'noindex,nofollow' | 'noindex,follow' | 'index,follow' {

  const createdWebcastDate = new Date(webcastData.start)
  const webcastCreatedYear = createdWebcastDate.getFullYear()
  const { 'player-preauth': playerPreauth } = query

  if (webcastCreatedYear < 2016 || webcastData.isGigCancelled || playerPreauth) {
    return 'noindex,nofollow'
  }

  if (webcastData.syndication?.syndicationIn || webcastData.visibility === 'private' || (webcastData.visibility === 'public' && channelData.visibility !== 'included')) {
    return 'noindex,follow'
  }

  return 'index,follow'

}

const MetaHead: FC = () => {

  const channel = useContext(ChannelContext)
  const webcast = useContext(WebcastContext)
  const { hostUrl } = useContext(EnvVariablesContext)
  const webcastPrimaryCommunity = getWebcastPrimaryCommunity(useContext(WebcastCommunitiesContext))
  const { asPath, query } = useRouter()
  const defaultRouterVars = getGigIDsFromAsPath(asPath)
  const {
    channelId = defaultRouterVars.channelId,
    webcastId = defaultRouterVars.webcastId
  } = useGigIDs()
  const canonicalUrl = getCanonicalUrl(hostUrl, channelId as string, webcastId as string)
  const structuredData = getStructuredData(channel, webcast, canonicalUrl)

  return <Head>
    <title>{webcast.title || 'There was a problem showing this webinar'}</title>
    <meta name='description' content={trimMetaDescription(webcast.description)} />
    <meta property='og:type' content='website' />
    <meta name='og:title' property='og:title' content={webcast.title} />
    <meta name='og:description' property='og:description' content={webcast.description} />
    <meta property='og:site_name' content='BrightTALK' />
    <meta property='og:url' content={canonicalUrl} />
    <meta name='brighttalk-channel-id' content={channelId} />
    <meta name='brighttalk-page-architecture' content='react' />
    <meta name='brighttalk-page-name' content='webcast' />
    <meta name='brighttalk-content-status' content={webcast.status} />
    <meta name='brighttalk-community' content={webcastPrimaryCommunity && webcastPrimaryCommunity.children ? webcastPrimaryCommunity.children[0].title : 'None'} />
    <meta name='brighttalk-community-parent' content={webcastPrimaryCommunity ? webcastPrimaryCommunity.title : 'None'} />
    <meta name='twitter:card' content='summary' />
    <meta name='twitter:title' content={webcast.title} />
    <meta name='twitter:description' content={webcast.description} />
    <meta name='twitter:site' content='BrightTALK' />
    <meta name='twitter:creator' content='' />
    <meta property='og:image' content={webcast.previewImg} />
    <meta name='twitter:image' content={webcast.previewImg} />
    <meta name='viewport' content='initial-scale=1.0, width=device-width' />
    <meta name='robots' content={getRobotsMeta(webcast, channel, query)}></meta>
    <script {...structuredData} />
    <script async src="https://unpkg.com/date-time-format-timezone@latest/build/browserified/date-time-format-timezone-complete-min.js"></script>
    <style type="text/css" dangerouslySetInnerHTML={{ __html: consentCssScript() }}></style>
    {/* <script type="text/javascript" dangerouslySetInnerHTML={{ __html: privacyScript() }}></script> */}
    {/* <script src='https://consent.brighttalk.com/unified/wrapperMessagingWithoutDetection.js' async></script> */}
    <script type="text/javascript" dangerouslySetInnerHTML={{ __html: consentScript() }}></script>
	<script src='https://cdn.privacy-mgmt.com/unified/wrapperMessagingWithoutDetection.js' async></script>
	{/* <script src="https://consent.brighttalk.com/ccpa.js"></script> */}
  </Head>

}

/*
  The date-time-format-timezone-complete CDN link is for IE11
*/

export default MetaHead
